import { Box, Breadcrumbs, Link, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { Navigate } from 'react-router-dom';

import { getAnnotationToolAdminProjectsPath } from '@verantos-dev/core-annotation-tool';

import { BatchTypeSection } from './components/BatchTypeSection/BatchTypeSection';
import { ExtractionModelSection } from './components/ExtractionModelSection/ExtractionModelSection';
import { ExtractionPromptSection } from './components/ExtractionPromptSection/ExtractionPromptSection';
import { GenerateAccuracyFooter } from './components/GenerateAccuracyFooter/GenerateAccuracyFooter';
import { JobRunDescriptionSection } from './components/JobRunDescriptionSection/JobRunDescriptionSection';
import { OptimizationProjectCard } from './components/OptimizationProjectCard/OptimizationProjectCard';
import { useGenerateAccuracyScreen } from './useGenerateAccuracyScreen';

export const GenerateAccuracyScreen = () => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const {
    canAccess,
    projectId,
    projectName,
    conditionAreaName,
    healthSystemName,
    isProjectLoading,
    batches,
    isBatchesLoading,
    isBatchesError,
    batchesError,
    models,
    isModelsLoading,
    isModelsError,
    modelsError,
    prompts,
    isPromptsLoading,
    isPromptsError,
    promptsError,
    isBranchPickerOn,
    branches,
    selectedBranch,
    isBranchesLoading,
    isBranchesError,
    branchesError,
    state,
    validation,
    runSummary,
    isSubmitting,
    submitError,
    handleChangeProject,
    handleSelectBatch,
    handleConfirmTestBatch,
    handleSwitchToValidation,
    handleSelectModel,
    handleSelectBranch,
    handleSelectPrompt,
    handleEditDescription,
    handleResetDescription,
    handleGenerate,
  } = useGenerateAccuracyScreen();

  // Checked inside the screen rather than via a route-level redirect: the flag reads
  // `false` until the Split SDK is ready, and a redirect wrapper would bounce an
  // authorised user before it settles. See docs/DESIGN_TRIGGER_ACCURACY_FROM_UI.md.
  // TODO: cleanup with cp_trigger_accuracy_from_ui ff
  if (!canAccess) {
    return <Navigate to={getAnnotationToolAdminProjectsPath()} replace />;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          maxWidth: 880,
          m: '0 auto',
          pb: theme.spacing(40),
        }}
      >
        <Box sx={{ flex: 1, px: theme.spacing(6), py: theme.spacing(4) }}>
          <Breadcrumbs
            id="create-project-wizard-breadcrumbs"
            sx={{ fontSize: '16px' }}
            separator="/"
          >
            <Link
              color="inherit"
              underline="hover"
              onClick={handleChangeProject}
              data-testid="breadcrumb-manage-projects"
              sx={{ cursor: 'pointer' }}
            >
              {formatMessage({ id: 'adminProjects.generateAccuracy.header.allProjects' })}
            </Link>
            <Link
              color="inherit"
              underline="none"
              data-testid="breadcrumb-create-project"
              sx={{ fontWeight: '500' }}
            >
              {formatMessage({ id: 'adminProjects.generateAccuracy.header.accuracyService' })}
            </Link>
          </Breadcrumbs>

          <Typography variant="h4" sx={{ mt: theme.spacing(1) }}>
            {formatMessage({ id: 'adminProjects.generateAccuracy.header.title' })}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mt: theme.spacing(1), mb: theme.spacing(5) }}
          >
            {formatMessage({ id: 'adminProjects.generateAccuracy.header.description' })}
          </Typography>

          <OptimizationProjectCard
            projectName={projectName}
            conditionAreaName={conditionAreaName}
            healthSystemName={healthSystemName}
            isLoading={isProjectLoading}
          />

          <Box sx={{ mt: theme.spacing(6) }}>
            <BatchTypeSection
              batches={batches}
              isLoading={isBatchesLoading}
              isError={isBatchesError}
              error={batchesError}
              selectedBatchId={state.batchId}
              isComplete={validation.batch}
              onSelectBatch={handleSelectBatch}
              onConfirmTestBatch={handleConfirmTestBatch}
              onSwitchToValidation={handleSwitchToValidation}
            />

            <ExtractionModelSection
              models={models}
              isLoading={isModelsLoading}
              isError={isModelsError}
              error={modelsError}
              selectedModelId={state.modelId}
              isComplete={validation.model}
              isBatchSelected={Boolean(state.batchId)}
              onSelectModel={handleSelectModel}
            />

            <ExtractionPromptSection
              prompts={prompts}
              isLoading={isPromptsLoading}
              isError={isPromptsError}
              error={promptsError}
              isModelSelected={Boolean(state.modelId)}
              projectId={projectId}
              selectedPromptPath={state.promptPath}
              isComplete={validation.prompt}
              onSelectPrompt={handleSelectPrompt}
              isBranchPickerOn={isBranchPickerOn}
              branches={branches}
              isBranchesLoading={isBranchesLoading}
              isBranchesError={isBranchesError}
              branchesError={branchesError}
              selectedBranch={selectedBranch}
              onSelectBranch={handleSelectBranch}
            />

            <JobRunDescriptionSection
              description={state.description}
              isDescriptionManuallyEdited={state.isDescriptionManuallyEdited}
              isComplete={validation.description}
              arePreviousInputsFilled={Boolean(state.batchId && state.modelId)}
              onChange={handleEditDescription}
              onReset={handleResetDescription}
            />
          </Box>
        </Box>
      </Box>

      <GenerateAccuracyFooter
        runSummary={runSummary}
        canGenerate={validation.canGenerate}
        isSubmitting={isSubmitting}
        submitError={submitError}
        onGenerate={handleGenerate}
      />
    </Box>
  );
};
